import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/contacts`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Failed to send message. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: 'Email',
      value: 'info@mohtech-cyber.co.ke',
      link: 'mailto:info@mohtech-cyber.co.ke'
    },
    {
      icon: <Phone size={24} />,
      title: 'Phone',
      value: '+254 721 677360',
      link: 'tel:+254721677360'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Location',
      value: 'Nairobi, Kenya',
      link: null
    },
    {
      icon: <Clock size={24} />,
      title: 'Business Hours',
      value: 'Mon - Fri: 8AM - 6PM',
      link: null
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section
        data-testid="contact-hero"
        className="relative py-24 lg:py-32 hero-bg"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1771011726574-60ab6028724a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaG5vbG9neSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0NjI5Mzg0fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="hero-overlay absolute inset-0" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-6">
              Get In Touch
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Let&apos;s Start a Conversation
            </h1>
            <p className="text-lg text-[#A1B0CC] leading-relaxed">
              Have a project in mind? We&apos;d love to hear from you. Send us a message 
              and we&apos;ll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section data-testid="contact-section" className="py-24 bg-[#050B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-[#0A1121] border border-white/5 rounded-2xl p-8 lg:p-12">
              <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#A1B0CC] mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    className="w-full bg-[#050B14] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-[#5B6B8A] focus:border-[#FF5E1A] focus:ring-1 focus:ring-[#FF5E1A] outline-none transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#A1B0CC] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="w-full bg-[#050B14] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-[#5B6B8A] focus:border-[#FF5E1A] focus:ring-1 focus:ring-[#FF5E1A] outline-none transition-all"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#A1B0CC] mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="contact-phone-input"
                    className="w-full bg-[#050B14] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-[#5B6B8A] focus:border-[#FF5E1A] focus:ring-1 focus:ring-[#FF5E1A] outline-none transition-all"
                    placeholder="+254 700 000000"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#A1B0CC] mb-2">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    className="w-full bg-[#050B14] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-[#5B6B8A] focus:border-[#FF5E1A] focus:ring-1 focus:ring-[#FF5E1A] outline-none transition-all resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full bg-[#FF5E1A] hover:bg-[#FF7A33] text-white font-bold rounded-full py-4 flex items-center justify-center gap-2 transition-all btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div className="space-y-8">
              {/* Contact Info Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    data-testid={`contact-info-${index}`}
                    className="bg-[#0A1121] border border-white/5 rounded-2xl p-6"
                  >
                    <div className="w-12 h-12 bg-[#FF5E1A]/10 rounded-xl flex items-center justify-center text-[#FF5E1A] mb-4">
                      {info.icon}
                    </div>
                    <h3 className="text-white font-semibold mb-1">{info.title}</h3>
                    {info.link ? (
                      <a
                        href={info.link}
                        className="text-[#A1B0CC] hover:text-[#FF5E1A] transition-colors text-sm"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-[#A1B0CC] text-sm">{info.value}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Google Map */}
              <div
                data-testid="google-map"
                className="bg-[#0A1121] border border-white/5 rounded-2xl overflow-hidden h-[300px]"
              >
                <iframe
                  title="Mohtech Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255282.3233734785!2d36.68225!3d-1.3028618!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f1172d84d49a7%3A0xf7cf0254b297924c!2sNairobi%2C%20Kenya!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              {/* WhatsApp CTA */}
              <div className="bg-[#25D366]/10 border border-[#25D366]/20 rounded-2xl p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">Prefer WhatsApp?</h3>
                  <p className="text-[#A1B0CC] text-sm">Chat with us directly for quick responses.</p>
                </div>
                <a
                  href="https://wa.me/254721677360?text=Hello!%20I%20would%20like%20to%20inquire%20about%20your%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-contact-btn"
                  className="bg-[#25D366] hover:bg-[#20c05c] text-white font-semibold rounded-full px-6 py-2.5 transition-all"
                >
                  Chat Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#0A1121]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-[#FF5E1A] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'How long does a typical project take?',
                a: 'Project timelines vary based on complexity. Simple websites take 2-4 weeks, while complex systems can take 2-6 months.'
              },
              {
                q: 'Do you offer maintenance and support?',
                a: 'Yes, we offer ongoing maintenance packages to keep your systems running smoothly and up-to-date.'
              },
              {
                q: 'What technologies do you work with?',
                a: 'We work with modern technologies including React, Python, Node.js, and various cloud platforms and APIs.'
              },
              {
                q: 'Can you integrate M-Pesa into my system?',
                a: 'Absolutely! M-Pesa integration is one of our specialties. We can integrate STK Push, C2B, B2C, and more.'
              },
            ].map((faq, index) => (
              <div
                key={index}
                data-testid={`faq-${index}`}
                className="bg-[#050B14] border border-white/5 rounded-xl p-6"
              >
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-[#A1B0CC] text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
